import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { pricingService } from '../../../services/pricingService';

const MarketplacePreview = ({ productData }) => {
  const [activeMarketplace, setActiveMarketplace] = useState('mercadolivre');

  const marketplaces = [
    { id: 'mercadolivre', label: 'ML', color: 'bg-yellow-400' },
    { id: 'shopee', label: 'Shopee', color: 'bg-orange-500' },
    { id: 'amazon', label: 'Amazon', color: 'bg-black' }
  ];

  const getMarketplacePrice = () => {
    const basePrice = parseFloat(productData?.price) || 0;
    let markup = 0;

    switch (activeMarketplace) {
      case 'mercadolivre':
        markup = parseFloat(productData?.mercadolivreMarkup) || 15;
        break;
      case 'shopee':
        markup = parseFloat(productData?.shopeeMarkup) || 12;
        break;
      case 'amazon':
        markup = parseFloat(productData?.amazonMarkup) || 18;
        break;
      default:
        markup = 0;
    }

    return basePrice * (1 + markup / 100);
  };

  const calculateMargin = () => {
    const cost = parseFloat(productData?.cost) || 0;
    const finalPrice = getMarketplacePrice();
    const profit = finalPrice - cost;
    
    return cost > 0 ? (profit / finalPrice) * 100 : 0;
  };

  const calculateCubicWeight = () => {
    const width = parseFloat(productData?.width) || 0;
    const height = parseFloat(productData?.height) || 0;
    const length = parseFloat(productData?.length) || 0;
    
    return (width * height * length) / 6000;
  };

  const getCompletionStatus = () => {
    const requiredFields = [
      productData?.name,
      productData?.sku,
      productData?.brand,
      productData?.category,
      productData?.price,
      productData?.weight,
      productData?.ncm,
      productData?.images?.length > 0
    ];

    const filledFields = requiredFields?.filter(Boolean)?.length;
    const totalFields = requiredFields?.length;
    const percentage = (filledFields / totalFields) * 100;

    return {
      percentage,
      status: percentage === 100 ? 'Completo' : 'Incompleto'
    };
  };

  const getPrimaryImage = () => {
    if (productData?.images && Array.isArray(productData?.images) && productData?.images?.length > 0) {
      return productData?.images?.[0];
    }
    return '/assets/images/no_image.png';
  };

  const finalPrice = getMarketplacePrice();
  const margin = calculateMargin();
  const cubicWeight = calculateCubicWeight();
  const completion = getCompletionStatus();

  return (
    <div className="p-6 space-y-6">
      {/* Marketplace Tabs */}
      <div>
        <h3 className="text-sm font-semibold text-foreground mb-3">Preview do Anúncio</h3>
        <div className="flex gap-2">
          {marketplaces?.map(mp => (
            <button
              key={mp?.id}
              onClick={() => setActiveMarketplace(mp?.id)}
              className={`
                flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-smooth
                ${activeMarketplace === mp?.id
                  ? `${mp?.color} text-white`
                  : 'bg-muted text-muted-foreground hover:bg-muted/70'
                }
              `}
            >
              {mp?.label}
            </button>
          ))}
        </div>
      </div>

      {/* Product Preview Card */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {/* Image */}
        <div className="aspect-square bg-muted">
          <img
            src={getPrimaryImage()}
            alt={productData?.name || 'Product preview'}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.src = '/assets/images/no_image.png';
            }}
          />
        </div>

        {/* Info */}
        <div className="p-4 space-y-3">
          <h4 className="font-semibold text-foreground line-clamp-2">
            {productData?.name || 'Nome do Produto'}
          </h4>

          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-primary">
              {pricingService?.formatCurrency(finalPrice)}
            </span>
          </div>

          <div className="space-y-2 text-xs text-muted-foreground">
            <div className="flex items-center gap-2">
              <Icon name="Package" size={12} />
              <span>Condição: {productData?.condition === 'novo' ? 'Novo' : productData?.condition}</span>
            </div>
            
            {productData?.warranty && (
              <div className="flex items-center gap-2">
                <Icon name="Shield" size={12} />
                <span>Garantia: {productData?.warranty} meses</span>
              </div>
            )}
            
            <div className="flex items-center gap-2">
              <Icon name="Truck" size={12} />
              <span>
                {activeMarketplace === 'mercadolivre' && productData?.mercadolivreFreeShipping
                  ? 'Frete Grátis' :'Frete a calcular'
                }
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Analysis Card */}
      <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-3">
        <h4 className="text-sm font-semibold text-foreground">Análise</h4>
        
        <div className="space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Margem de Lucro</span>
            <span className={`font-semibold ${
              margin >= 30 ? 'text-success' : margin >= 15 ? 'text-warning' : 'text-destructive'
            }`}>
              {margin?.toFixed(1)}%
            </span>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Peso Cubado</span>
            <span className="font-semibold text-foreground">
              {cubicWeight?.toFixed(2)} kg
            </span>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Status dos Campos</span>
            <span className={`font-semibold ${
              completion?.percentage === 100 ? 'text-success' : 'text-warning'
            }`}>
              {completion?.status}
            </span>
          </div>
        </div>

        {/* Progress Bar */}
        <div>
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
            <span>Preenchimento</span>
            <span>{completion?.percentage?.toFixed(0)}%</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div
              className={`h-full transition-smooth ${
                completion?.percentage === 100 ? 'bg-success' : 'bg-warning'
              }`}
              style={{ width: `${completion?.percentage}%` }}
            />
          </div>
        </div>
      </div>

      {/* Validation Messages */}
      {completion?.percentage < 100 && (
        <div className="bg-warning/10 border border-warning/20 rounded-lg p-3">
          <div className="flex gap-2">
            <Icon name="AlertCircle" size={16} className="text-warning mt-0.5" />
            <div>
              <p className="text-xs font-medium text-foreground">Campos Pendentes</p>
              <p className="text-xs text-muted-foreground mt-1">
                Preencha todos os campos obrigatórios para publicar
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketplacePreview;